/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { TFunction } from 'i18next';
import {
  FF_APP_FACEBOOK, FF_APP_AGENCY_SITE, FF_APP_LEASE, FF_APP_SALE,
} from '@/shared/utils/feature-flag';

export type SettingsSection =
  | 'perfil' | 'equipe' | 'permissoes' | 'plano' | 'portais' | 'facebook' | 'vendas'
  | 'locacoes' | 'imoveis';

export interface SettingsNavItem {
  key: SettingsSection;
  label: string;
  group: string;
}

/** Options that gate which settings sections are reachable. */
export interface SettingsNavOptions {
  /**
   * True for solo-broker accounts, where the single user always holds every
   * permission and cannot invite members — so team and permission sections
   * have nothing to show.
   */
  isSoloBroker?: boolean;
  /**
   * Whether the current member may manage the sale paperwork vocabulary —
   * the `manage_sale` permission, held by owner, admin and operational.
   * A broker only records dates against the milestones, so the section
   * that defines them is not theirs to see. Defaults to false so a caller
   * that forgets to pass it hides the section rather than exposing it.
   */
  canManageSalePaperwork?: boolean;
  /**
   * Whether the current member may configure Locações — the `manage_rent`
   * permission, held by owner, admin and operational. Defaults to false so a
   * caller that forgets to pass it hides the section rather than exposing it.
   */
  canManageLeaseSettings?: boolean;
  /**
   * Whether the current member may choose which property fields a broker
   * must fill — the `manage_property_settings` permission, held by owner,
   * admin and operational. Defaults to false so a caller that forgets to
   * pass it hides the section rather than exposing it.
   */
  canManagePropertySettings?: boolean;
}

/** Sections that only make sense for multi-member agencies. */
const MULTI_MEMBER_SECTIONS: SettingsSection[] = ['equipe', 'permissoes'];

/**
 * Per-section visibility predicates for the flag/permission-gated sections.
 * Sections not listed here are always visible (subject to the solo-broker
 * guard applied first in {@link isSettingsSectionVisible}).
 */
const GATED_SECTION_VISIBILITY: Partial<
  Record<SettingsSection, (options: SettingsNavOptions) => boolean>
> = {
  facebook: () => FF_APP_FACEBOOK,
  vendas: (options) => FF_APP_SALE && Boolean(options.canManageSalePaperwork),
  locacoes: (options) => FF_APP_LEASE && Boolean(options.canManageLeaseSettings),
  imoveis: (options) => Boolean(options.canManagePropertySettings),
};

/**
 * Whether a settings section is reachable for the given account shape.
 *
 * @param section - The settings section key.
 * @param options - Account-shape options.
 * @returns True when the section should be listed.
 */
export function isSettingsSectionVisible(section: SettingsSection, options: SettingsNavOptions = {}): boolean {
  if (options.isSoloBroker && MULTI_MEMBER_SECTIONS.includes(section)) return false;
  const gate = GATED_SECTION_VISIBILITY[section];
  return gate ? gate(options) : true;
}

/**
 * Builds the ordered list of settings navigation items with localized labels.
 * The Facebook item is included only when its feature flag is enabled, and the
 * team/permission items are omitted for solo-broker accounts.
 *
 * @param t - Translation function.
 * @param options - Account-shape options that gate sections.
 * @returns The navigation items to render in the settings sidebar.
 */
export function getSettingsNav(t: TFunction, options: SettingsNavOptions = {}): SettingsNavItem[] {
  const allItems: SettingsNavItem[] = [
    { key: 'perfil', label: t('v2.nav.perfil'), group: t('v2.nav.groups.geral') },
    { key: 'equipe', label: t('v2.nav.equipe'), group: t('v2.nav.groups.geral') },
    { key: 'permissoes', label: t('v2.nav.permissoes'), group: t('v2.nav.groups.geral') },
    { key: 'plano', label: t('v2.nav.plano'), group: t('v2.nav.groups.conta') },
    { key: 'portais', label: t('v2.nav.portais'), group: t('v2.nav.groups.integracoes') },
    { key: 'facebook', label: t('v2.nav.facebook'), group: t('v2.nav.groups.integracoes') },
    { key: 'imoveis', label: t('v2.nav.imoveis'), group: t('v2.nav.groups.operacoes') },
    { key: 'vendas', label: t('v2.nav.vendas'), group: t('v2.nav.groups.operacoes') },
    { key: 'locacoes', label: t('v2.nav.locacoes'), group: t('v2.nav.groups.operacoes') },
  ];

  return allItems.filter((item) => isSettingsSectionVisible(item.key, options));
}

/**
 * Returns the distinct navigation group names, in display order.
 *
 * @param t - Translation function.
 * @param options - Account-shape options that gate sections.
 * @returns The unique group labels.
 */
export function getSettingsGroups(t: TFunction, options: SettingsNavOptions = {}): string[] {
  return [...new Set(getSettingsNav(t, options).map((item) => item.group))];
}
